import { Type, Transform, plainToInstance } from 'class-transformer';
import { DunningState } from './dunning_state';
import { Model } from './model';
import type dayjs from 'dayjs';
import { TransformStringToDayJS } from './utils';
import { Transcript } from './call';

export interface ContactTaskDataJSON {
  email_address: string;

  given_name: string;

  is_primary: boolean;

  family_name: string;
}

export interface OrganizationJSON {
  id: string;

  name: string;

  ar_team_size: string;
}

export interface PartnerJSON {
  id: string;

  name: string;

  contacts: string[];
}

export interface EmailJSON {
  id: string;

  body: string;

  sender: string;

  sent_at: Date;

  subject: string;

  thread_id: string;
}

export interface SMSJSON {
  id: string;

  sms_thread_id: string;

  from_number: string;

  to_number: string;

  sent_at: Date;

  body: string;
}

export enum PaymentType {
  ACH = 'ach',
  WIRE = 'wire',
  CHECK = 'check',
  OTHER = 'other',
}

export class AcknowledgePromise {
  amount: number;

  sent_date: Date | null;

  payment_type: PaymentType;

  promised_date: Date | null;

  invoice_number: string;

  invoice_id: string;

  is_calculated: boolean;

  outside_task_scope: boolean;

  unknown_invoice_number: boolean;

  is_promised_date_updated: boolean;
}

export class DisputeContextData {
  partner_id?: string;

  invoice_id?: string;

  invoice_number?: string;

  matched_option_id?: string;

  disputed_amount?: number;

  currency?: string;

  priority?: string;

  assignee_id?: string;

  description?: string;
}

export interface DisableCommunicationActions {
  id: string;

  auto_disable: boolean;
}

export interface NewPrimaryContact {
  contact_value: string;

  id: string;
}

export class PauseStateUpdateInfo extends Model {
  @Type(() => DunningState)
  old_state?: DunningState;

  @Type(() => DunningState)
  new_state: DunningState;
}

export class AcknowledgeTaskDataJSON {
  promises: AcknowledgePromise[];

  agent_uncertainties: string[];

  agent_confidence_score: number;

  agent_confidence_reason: string;

  call_id: string;

  contact_id: string;

  contact_value: string;

  email_id: string;

  is_primary_disabled: string;

  new_primary_contact: NewPrimaryContact;

  disable_communication_actions: DisableCommunicationActions[];

  partner_id: string;

  reason: string;

  source: string;

  @Type(() => PauseStateUpdateInfo)
  pause_state_update_info?: PauseStateUpdateInfo;
}

export interface ResponseTaskDataJSON {
  cc: string[];

  to: string[];

  body: string;

  subject: string;

  from_: string;

  html_body: string;
}

export interface SummaryTaskDataJSON {
  summary: string;

  confidence: number;

  disposition: string;

  consent_given: boolean;
}

export class TaskCallSegment extends Model {
  content: string;

  end_time: number;

  role: string;

  start_time: number;
}

export class TaskCall extends Model {
  segments?: TaskCallSegment[];
  duration_ms: number;
  retell_recording_url: string;
  id: string;
  from_number: string;
  to_number: string;
  status: string;

  @TransformStringToDayJS()
  start_time: dayjs.Dayjs | null;

  @TransformStringToDayJS()
  end_time: dayjs.Dayjs | null;

  @Type(() => Transcript)
  transcript: Transcript[] | null;

  recording_url: string | null;
  transcript_str: string | null;
  summary: string;
  sentiment: string;
  in_voicemail: boolean;
  partner_id: string | null;
  call_reason: string | null;
  disconnection_reason: string | null;
  recording_consent_granted?: boolean | null;
}

export abstract class TaskData extends Model {
  id: string;

  type: string;

  source: 'email' | 'phone' | 'sms';

  task_id: string;
}

export class EmailContextJSON extends Model {
  email: EmailJSON;

  organization: OrganizationJSON;

  partner: PartnerJSON;
}

export class PhoneContextJSON extends Model {
  organization: OrganizationJSON;

  partner: PartnerJSON;

  @Type(() => TaskCall)
  call?: TaskCall;
}

export class SMSContextJSON extends Model {
  sms: SMSJSON;

  organization: OrganizationJSON;

  partner: PartnerJSON;
}

export class ContextTaskData extends TaskData {
  @Transform(({ value, obj }) => {
    if (value?.dispute) {
      return plainToInstance(DisputeContextData, value?.dispute as DisputeContextData);
    }

    if (obj.source === 'phone') {
      if (value instanceof PhoneContextJSON === false) {
        return plainToInstance(PhoneContextJSON, value);
      }
    }

    if (obj.source === 'email') {
      if (value instanceof EmailContextJSON === false) {
        return plainToInstance(EmailContextJSON, value);
      }
    }

    if (obj.source === 'sms') {
      if (value instanceof SMSContextJSON === false) {
        return plainToInstance(SMSContextJSON, value);
      }
    }

    return value;
  })
  data: EmailContextJSON | PhoneContextJSON | SMSContextJSON | DisputeContextData;
}

export class DefaultTaskData extends TaskData {
  data: Record<string, any>;
}

export class ResponseTaskData extends TaskData {
  data: ResponseTaskDataJSON;
}

export class SummaryTaskData extends TaskData {
  data: SummaryTaskDataJSON;
}

export class ContactTaskData extends TaskData {
  data: ContactTaskDataJSON;
}

export class AcknowledgeTaskData extends TaskData {
  data: AcknowledgeTaskDataJSON;

  get promiseData() {
    return this.data?.promises;
  }

  get hasOtherPromises() {
    return !!this.outsideTaskPromises?.length || !!this.unknownInvoicePromises?.length;
  }

  get outsideTaskPromises() {
    return this.promiseData?.filter(
      (promise) => promise.outside_task_scope && !promise.unknown_invoice_number,
    );
  }

  get unknownInvoicePromises() {
    return this.promiseData?.filter((promise) => promise.unknown_invoice_number);
  }
}
